import { z } from "zod";

import type { OnboardingUser } from "@/features/onboarding/models";
import type { UrlParamsSlice } from "@/features/onboarding/stores/url-params-slice";
import type { CreditAmount } from "@/features/referral/models";
import { Subscription } from "@/shared/domains";
import type { Coupon, PaymentMethod, Price } from "@/shared/domains";
import { parseResponseData, privateAPI, publicAPI } from "@/shared/lib";

const paymentIntentSchema = z.object({
  subscription: Subscription,
  clientSecret: z.string().nullable(),
  paymentIntentId: z.string().nullable(),
  invoiceId: z.string().nullable(),
});

interface CreatePaymentsParams {
  prices: Price["id"][];
  discount?: Coupon["code"];
  metadata?: UrlParamsSlice["urlParams"];
  paymentMethod?: PaymentMethod["type"];
}

export const createOnboardingPayments = async ({
  userCode,
  shipping,
  ...params
}: CreatePaymentsParams &
  Pick<OnboardingUser, "state" | "shipping"> & {
    userCode: OnboardingUser["code"];
  }) => {
  const response = await publicAPI.post(`/patients/${userCode}/checkout`, {
    ...params,
    city: shipping.city,
    line1: shipping.address,
    line2: shipping.apartmentNumber,
    zip: shipping.zipCode,
  });

  return parseResponseData(response, paymentIntentSchema.array());
};

export const createShopPayments = async (
  params: CreatePaymentsParams & {
    referralCredit?: CreditAmount;
  },
) => {
  const response = await privateAPI.post(`/shop/checkout`, params);

  return parseResponseData(response, paymentIntentSchema.array());
};

export const recentPayments = async (userCode: OnboardingUser["code"]) => {
  const response = await publicAPI.get(`/patients/${userCode}/purchases`);

  return parseResponseData(response, paymentIntentSchema.array());
};
